import { schema } from 'normalizr';

const CUSTOMERS = 'customers';
export const CUSTOMERS_SCHEMA = new schema.Entity(CUSTOMERS);

const NOTES = 'notes';
export const NOTES_SCHEMA = new schema.Entity(NOTES);

const FILES = 'files';
export const FILES_SCHEMA = new schema.Entity(FILES);

const SUBTASKS = 'subtasks';
export const SUBTASKS_SCHEMA = new schema.Entity(SUBTASKS);

const APPOINTMENTS = 'appointments';
export const APPOINTMENTS_SCHEMA = new schema.Entity(APPOINTMENTS);

const MESSAGES = 'messages';
export const MESSAGES_SCHEMA = new schema.Entity(MESSAGES);

const MATCH_RELATIONS = {
  [NOTES]: [NOTES_SCHEMA],
  [FILES]: [FILES_SCHEMA],
  [MESSAGES]: [MESSAGES_SCHEMA],
};

const MATCHES = 'matches';
export const MATCHES_SCHEMA = new schema.Entity(
  MATCHES,
  MATCH_RELATIONS,
  {
    idAttribute: ({ skills_profile }, { id }) => `${skills_profile}-${id}`,
  }
);
export const MATCHES_WITH_APPOINTMENTS_SCHEMA = new schema.Entity(
  MATCHES,
  {
    ...MATCH_RELATIONS,
    [APPOINTMENTS]: [APPOINTMENTS_SCHEMA],
  },
  {
    idAttribute: ({ skills_profile }, { id }) => `${skills_profile}-${id}`,
  }
);

const JOBS = 'jobs';
export const JOBS_SCHEMA = new schema.Entity(JOBS, {
  customer: CUSTOMERS_SCHEMA,
  [MATCHES]: [MATCHES_SCHEMA],
  subtask: SUBTASKS_SCHEMA,
});

export const JOBS_WITH_APPOINTMENTS_SCHEMA = new schema.Entity(JOBS, {
  customer: CUSTOMERS_SCHEMA,
  [MATCHES]: [MATCHES_WITH_APPOINTMENTS_SCHEMA],
  subtask: SUBTASKS_SCHEMA,
});

export const MATCHES_WITH_JOB_SCHEMA = new schema.Entity(
  MATCHES,
  {
    job: JOBS_SCHEMA,
  },
  {
    idAttribute: ({ skills_profile, job: { id } }) => `${skills_profile}-${id}`,
  }
);

export const NEW_MATCHES_SCHEMA = new schema.Entity(MATCHES);

const USER = 'user';
export const USER_SCHEMA = new schema.Entity(USER, {}, { idAttribute: ({ user_id }) => user_id });

const WORK_ORDERS = 'workOrders';
export const WORK_ORDERS_SCHEMA = new schema.Entity(
  WORK_ORDERS,
  {
    [MATCHES]: [NEW_MATCHES_SCHEMA],
    [USER]: USER_SCHEMA,
  },
  {
    idAttribute: ({ user_friendly_identifier }) => user_friendly_identifier,
  }
);

const PUBLIC_PROFILES = 'publicProfiles';
export const PUBLIC_PROFILES_SCHEMA = new schema.Entity(PUBLIC_PROFILES);

const PRO_WORK_ORDER_MATCHES = 'proWorkOrdersMatches';
export const PRO_WORK_ORDER_MATCHES_SCHEMA = new schema.Entity(
  PRO_WORK_ORDER_MATCHES,
  {
    [NOTES]: [NOTES_SCHEMA],
    [APPOINTMENTS]: [APPOINTMENTS_SCHEMA],
  }
);

export const PRO_WORK_ORDERS = 'proWorkOrders';
export const PRO_WORK_ORDERS_SCHEMA = new schema.Entity(
  PRO_WORK_ORDERS,
  {
    [MATCHES]: PRO_WORK_ORDER_MATCHES_SCHEMA,
  },
  {
    idAttribute: ({ order_hash }) => order_hash,
  }
);

export const ENTITY_TYPES = {
  ...MATCH_RELATIONS,
  [JOBS]: JOBS_SCHEMA,
  [CUSTOMERS]: CUSTOMERS_SCHEMA,
  [APPOINTMENTS]: APPOINTMENTS_SCHEMA,
  [MATCHES]: MATCHES_SCHEMA,
  [SUBTASKS]: SUBTASKS_SCHEMA,
  [WORK_ORDERS]: WORK_ORDERS_SCHEMA,
  [USER]: USER_SCHEMA,
  [PUBLIC_PROFILES]: PUBLIC_PROFILES_SCHEMA,
  [PRO_WORK_ORDER_MATCHES]: PRO_WORK_ORDER_MATCHES_SCHEMA,
  [PRO_WORK_ORDERS]: PRO_WORK_ORDERS_SCHEMA,
};

const reduceEntityTypes = (keyValueFunctor, types = Object.keys(ENTITY_TYPES)) =>
  types.reduce((acc, type) => ({
    ...acc,
    [type]: keyValueFunctor(type),
  }), {});

const twoLevelDeepMergeReducer = (acc, [key, value]) => ({
  ...acc,
  [key]: value ? { ...acc[key], ...value } : null,
});

/**
 * Dear my friend! You've reached the file with entities module implementation.
 * I am fascinated by your curiosity! Let me tell how it works:
 *   1. The implementation of the module is driven by schemas, which are available in ProReferral project.
 *      If you need to add a new entity type - simply define a schema and register it in ENTITY_TYPES variable.
 *      It will simply generate empty initial state, getters, and mutations. You don't need to do anything else.
 *   2. There is only one action - patch. Patching entities store will be performed by merging on two levels deep.
 *   3. If you need to delete some value by any reason, just pass null.
 *   4. Your schemas should NOT be having circular dependencies! If so, it will cause an infinite loop in attempt
 *      to update observers.
 *   5. Some of the API responses might have already normalized resources. In that case, you don't need it to normalize again.
 */
export default {
  namespaced: true,
  state: {
    ...reduceEntityTypes(() => ({})),
  },
  getters: {
    ...reduceEntityTypes(type => state => state[type]),
  },
  actions: {
    patch ({ commit }, payload) {
      Object.entries(payload).forEach(([key, value]) => commit(key, value));
    },
  },
  mutations: {
    ...reduceEntityTypes(type => (state, payload) => {
      state[type] = Object.entries(payload).reduce(twoLevelDeepMergeReducer, state[type] || {});
    }),
  },
};
